import GeneralOptions from "slotworks/components/betbar/vo/general-options";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import Currency from "slotworks/i18n/currency";
import BetModel from "slotworks/model/bets/bet-model";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import wrapperMediator from "slotworks/integration/revolver/wrapper/wrapper-mediator";
import ls from "slotworks/utils/local-storage";

export enum RealityMode {
    FREE = "free_play",
    REAL = "real_play"
}

export enum Jurisdiction {
    UK = "uk",
    SE = "se",
    NL = "nl",
    DE = "de",
    NONE = "none"
}

class SettingsModel {
    public saveStake: boolean = true;

    public playMode: RealityMode = RealityMode.FREE;
    public language: string = "en";
    public homeURL: string = "#";
    public historyURL: string = "#";

    public sessionLimit: number = 0;
    public sessionLimitStep: number = 15;
    public sessionStartTime: number;

    public usesLines: boolean = false; // if this particular game uses lines, used for saving and loading

    public rtp: number;
    public maxWin: number;
    public maxWinProbability: number;
    public maxWinDelimiter: number = 1;

    public disableSessionCheck: boolean = false;
    public rcdisabled: boolean = false;

    public minSpinTime: number = 0;
    public midSpinTime: number = 0;
    
    public minSkipTime: number = 0;

    public hideUkJurisdictionBar: boolean = false;
    public jurisdictionSetLanguage: boolean = false;

    public buyoutEngine: boolean = false;

    public socialSweepstakes: boolean = false;
    public availableSocialCurrencies: string[] = [];
    public rtpdisplay: boolean = true;
    public historymenu: boolean = true;

    // $1.00 => $1 removing the numbers after the point for social currencies 100x
    public removeTrailingDecimals: boolean = false;

    // These are private so getters must be used, giving us the opportunity to save to local stoage
    private spaceToSpin: boolean = true;
    private leftHanded: boolean = false;
    private displayCoins: boolean = false;
    private showIntro: boolean = true;
    private quickSpin: boolean = false;

    private jurisdiction: Jurisdiction;

    private isLoaded: boolean;

    private skipDisabled: boolean = false;
    private quickSpinDisabled: boolean = false;

    public authData: any; // TODO temporary
    public betState: any; // TODO

    public startSession() {
        this.sessionStartTime = Date.now();
    }

    public getSessionLength(): number {
        const timeNow = Date.now();

        return Math.round((timeNow - this.sessionStartTime) / 60000);
    }

    public setInitialSessionLimit() {
        if(wrapperMediator.getBranding() == 'relax') {
            return;
        }
        let sessionLimit = revolverModel.getSessionLimit();
        let timeElapsed = revolverModel.getSessionLimitElapsed();
        if(sessionLimit) {
            this.sessionLimit = Math.round(sessionLimit/60);
            if(!timeElapsed) timeElapsed = 0;
            this.sessionStartTime = Date.now() - timeElapsed*1000;
        }
    }

    public setLeftHanded(value: boolean) {
        this.leftHanded = value;
        CanvasManager.setLeftHanded(this.getLeftHanded());
        this.save();
    }

    public getLeftHanded() {
        return this.leftHanded;
    }

    public setSpaceToSpin(value: boolean) {
        this.spaceToSpin = value;
        this.save();
    }

    public getSpaceToSpin() {
        return this.spaceToSpin;
    }

    public setDisplayCoins(value: boolean) {
        this.displayCoins = value;
        Currency.credits = this.getDisplayCoins();
        this.save();
    }

    public getDisplayCoins() {
        return this.displayCoins;
    }

    public setShowIntro(value: boolean) {
        this.showIntro = value;
        this.save();
    }

    public getQuickSpin() {
        if (this.isQuickSpinDisabled()) {
            return false;
        }
        return this.quickSpin;
    }

    public setQuickSpin(value: boolean) {
        this.quickSpin = value;
        this.save();
    }

    public getShowIntro() {
        return this.showIntro;
    }

    public load() {
        if (ls) {
            const leftHanded = ls.get("leftHanded");
            const displayCoins = ls.get("displayCoins");
            const showIntro = ls.get("showIntro");
            const spaceToSpin = ls.get("spaceToSpin");
            const quickSpin = ls.get("quickSpin");

            if (leftHanded !== null) {
                this.setLeftHanded(leftHanded);
            }

            if (spaceToSpin !== null) {
                this.spaceToSpin = spaceToSpin;
            }

            if (displayCoins !== null) {
                this.setDisplayCoins(displayCoins);
            }

            if (showIntro !== null) {
                this.setShowIntro(showIntro);
            }

            if (quickSpin !== null) {
                this.setQuickSpin(quickSpin);
            }
        }

        this.isLoaded = true;
    }

    public loadBet() {
        if (ls) {
            if (this.saveStake) {
                const betPerLine = ls.get("betPerLine");
                const selectedLines = ls.get("selectedLines");
                const coinSize = ls.get("coinSize");

                if (betPerLine !== null) {
                    if(BetModel.isValidBetPerLine(betPerLine)) {
                        BetModel.setBetPerLine(betPerLine);
                    } else {
                        ls.remove("betPerLine");
                    }
                }

                if (selectedLines !== null && this.usesLines) {
                    if(BetModel.isValidSelectedLines(selectedLines)) {
                        BetModel.setSelectedLines(selectedLines);
                    } else { 
                        ls.remove("selectedLines");
                    }
                }

                if (coinSize !== null) {
                    if(BetModel.isValidCoinSize(coinSize)) {
                        BetModel.setCoinSize(coinSize);
                    } else {
                        ls.remove("coinSize");
                    }
                }
            }
        }
    }

    public setJurisdiction(jurisdiction: Jurisdiction) {
        if (this.jurisdiction) {
            document.documentElement.classList.toggle(this.jurisdiction, false);
        }

        this.jurisdiction = jurisdiction;

        document.documentElement.classList.toggle(this.jurisdiction, true);
    }

    public isUKJurisdiction() {
        return this.jurisdiction === Jurisdiction.UK;
    }

    public isJurisdiction(jurisdiction: Jurisdiction | Jurisdiction[]) {
        let check = (jur: Jurisdiction) => this.jurisdiction === jur;
        if(Array.isArray(jurisdiction)) {
            return jurisdiction.some(string => check(string));
        }
        return check(jurisdiction);
    }

    public isSkipDisabled() {
        return this.skipDisabled;
    }

    public setSkipDisabled(skipDisabled:boolean) {
        this.skipDisabled = skipDisabled;
        document.documentElement.classList.toggle("no_skip", skipDisabled);
    }

    public isQuickSpinDisabled() {
        return this.quickSpinDisabled;
    }

    public setQuickSpinDisabled(quickSpinDisabled: boolean) {
        this.quickSpinDisabled = quickSpinDisabled;
        document.documentElement.classList.toggle("no_quick_spin", quickSpinDisabled);
    }

    public updateFromOptions(generalOptions: GeneralOptions) {
        if (generalOptions.sessionLimit < this.getSessionLength() || this.sessionLimit === 0) {
            this.startSession();
        }
        this.sessionLimit = generalOptions.sessionLimit;
        this.setDisplayCoins(generalOptions.displayCoins);
        this.setLeftHanded(generalOptions.leftHanded);
        this.setShowIntro(generalOptions.showIntro);

        this.quickSpin = generalOptions.quickSpin;
        this.spaceToSpin = generalOptions.spaceToSpin;

        this.save();
    }

    public generateGeneralOptions() {
        const generalOptions = new GeneralOptions();

        generalOptions.displayCoins = this.getDisplayCoins();
        generalOptions.leftHanded = this.getLeftHanded();
        generalOptions.showIntro = this.getShowIntro();
        generalOptions.spaceToSpin = this.spaceToSpin;

        generalOptions.quickSpin = this.quickSpin;
        generalOptions.sessionLimit = this.sessionLimit;
        generalOptions.sessionLimitStep = this.sessionLimitStep;
        generalOptions.sessionLimitMin = 0;
        generalOptions.sessionLimitMax = 120;

        return generalOptions;
    }

    public flush() {
        this.saveBet();
    }

    public isCurrencyInAvailableSocialCurrencies(currency: string) {
        return this.availableSocialCurrencies.indexOf(currency) >= 0;
    }

    private save() {
        if (this.isLoaded) {
            ls.set("leftHanded", this.leftHanded);
            ls.set("displayCoins", this.displayCoins);
            ls.set("showIntro", this.showIntro);
            ls.set("spaceToSpin", this.spaceToSpin);
            ls.set("quickSpin", this.quickSpin);
        }
    }

    private saveBet() {
        if(this.isLoaded) {
            if (this.saveStake) {
                ls.set("betPerLine", BetModel.getBetPerLine());
                ls.set("selectedLines", BetModel.getSelectedLines());
                ls.set("coinSize", BetModel.getCoinSize());
            }
        }
    }
}

const settingsModel = new SettingsModel();
settingsModel.setJurisdiction(Jurisdiction.UK);

export default settingsModel;



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/model/settings/settings-model.ts